import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MenuService } from 'src/app/Services/menu.service';
import { MenuItem, RoleMenuSetupRole } from 'src/app/Shared/Models/Menu.model';
import { EmptyStateComponent } from 'src/app/Shared/Components/empty-state/empty-state.component';
import { NotificationService } from 'src/app/Shared/prime-ng/notification.service';
import { PrimeNgModule } from 'src/app/Shared/prime-ng/prime-ng.module';

interface MenuGroupView {
  key: string;
  label: string;
  icon: string;
  items: MenuItem[];
}

@Component({
  selector: 'app-menu-management',
  standalone: true,
  imports: [CommonModule, FormsModule, PrimeNgModule, EmptyStateComponent],
  templateUrl: './menu-management.component.html',
  styleUrl: './menu-management.component.css'
})
export class MenuManagementComponent implements OnInit {
  public roles: RoleMenuSetupRole[] = [];
  public menus: MenuItem[] = [];
  public topLevelMenus: MenuItem[] = [];
  public groupedMenus: MenuGroupView[] = [];
  public selectedRole: RoleMenuSetupRole | null = null;
  public selectedMenuKeys = new Set<string>();
  public loading = false;
  public saving = false;

  constructor(
    private menuService: MenuService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.loadSetup();
  }

  public onRoleChange(role: RoleMenuSetupRole | null): void {
    this.selectedRole = role;
    this.selectedMenuKeys = new Set(role?.menuKeys ?? []);
  }

  public isSelected(menuKey: string): boolean {
    return this.selectedMenuKeys.has(menuKey);
  }

  public toggleMenu(menuKey: string, checked: boolean): void {
    if (checked) {
      this.selectedMenuKeys.add(menuKey);
    } else {
      this.selectedMenuKeys.delete(menuKey);
    }
  }

  public selectAll(): void {
    this.selectedMenuKeys = new Set(this.menus.map(menu => menu.key));
  }

  public clearAll(): void {
    this.selectedMenuKeys = new Set();
  }

  public save(): void {
    if (!this.selectedRole) {
      this.notification('Please select a role first', 'warning');
      return;
    }

    this.saving = true;
    this.menuService.updateRoleMenus(this.selectedRole.roleId, [...this.selectedMenuKeys]).subscribe({
      next: () => {
        this.selectedRole!.menuKeys = [...this.selectedMenuKeys];
        this.saving = false;
        this.notification('Menu setup saved', 'success');
        this.menuService.loadCurrentMenus().subscribe();
      },
      error: () => {
        this.saving = false;
        this.notification('Error saving menu setup', 'error');
      }
    });
  }

  public trackByMenuKey(_: number, item: MenuItem): string {
    return item.key;
  }

  public trackByGroupKey(_: number, group: MenuGroupView): string {
    return group.key;
  }

  private loadSetup(): void {
    this.loading = true;
    this.menuService.getSetup().subscribe({
      next: setup => {
        this.roles = setup.roles ?? [];
        this.menus = [...(setup.menus ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
        this.buildMenuGroups();
        this.onRoleChange(this.roles[0] ?? null);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notification('Error loading menu setup', 'error');
      }
    });
  }

  private buildMenuGroups(): void {
    this.topLevelMenus = this.menus.filter(menu => !menu.groupKey);

    const groups = new Map<string, MenuGroupView>();
    for (const menu of this.menus.filter(item => item.groupKey)) {
      const key = menu.groupKey!;
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label: menu.groupLabel || key,
          icon: menu.groupIcon || 'folder',
          items: [],
        });
      }

      groups.get(key)!.items.push(menu);
    }

    this.groupedMenus = [...groups.values()]
      .map(group => ({
        ...group,
        items: group.items.sort((a, b) => a.sortOrder - b.sortOrder),
      }))
      .sort((a, b) => a.items[0].sortOrder - b.items[0].sortOrder);
  }

  private notification(message: string, state: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      animation: { type: 'fade', duration: 400 },
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: state, icon: true }
    });
  }
}
